import React, { useState } from 'react';
import { MapPin, Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useWines } from '@/contexts/WinesContext';
import { doc, serverTimestamp, writeBatch } from '@/lib/firebase';
import { db } from '@/lib/firebase';

interface BulkLocationAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  wineIds: string[];
  wineNames?: string[];
}

export const BulkLocationAssignmentDialog: React.FC<BulkLocationAssignmentDialogProps> = ({
  isOpen,
  onClose,
  wineIds,
  wineNames,
}) => {
  const { locations, wines } = useWines();
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    setSelectedLocationId('');
    setLoading(false);
    setSuccess(false);
    onClose();
  };

  const handleAssign = async () => {
    if (wineIds.length === 0) return;

    setLoading(true);

    try {
      const batch = writeBatch(db);

      // Filtra vini bevuti - non possono avere una posizione fisica
      const cellarWineIds = wineIds.filter(wineId => {
        const wine = wines.find(w => w.id === wineId);
        return wine?.status === 'cellar';
      });

      if (cellarWineIds.length === 0) {
        alert('I vini bevuti non possono avere una posizione');
        setLoading(false);
        return;
      }

      for (const wineId of cellarWineIds) {
        const wineRef = doc(db, 'wines', wineId);

        const updateData: any = {
          updatedAt: serverTimestamp(),
        };

        // Update locationId (can be empty to remove location)
        if (selectedLocationId === 'remove') {
          updateData.locationId = null;
        } else if (selectedLocationId) {
          updateData.locationId = selectedLocationId;
        }

        batch.update(wineRef, updateData);
      }

      await batch.commit();

      setSuccess(true);

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error assigning location:', error);
      alert('Errore durante l\'assegnazione della posizione');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const locationOptions = [
    { value: '', label: 'Seleziona una posizione' },
    { value: 'remove', label: '🗑️ Rimuovi posizione' },
    ...locations.map(loc => ({
      value: loc.id,
      label: loc.name,
    })),
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Assegna Posizione</h2>
            <p className="text-sm text-gray-600 mt-1">
              Assegna una posizione a {wineIds.length} {wineIds.length === 1 ? 'vino' : 'vini'} selezionat{wineIds.length === 1 ? 'o' : 'i'}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {success ? (
              /* Success State */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Posizione Aggiornata!
                </h3>
                <p className="text-sm text-gray-600">
                  {wineIds.length === 1 ? 'Il vino è' : `I ${wineIds.length} vini sono`} stato/i aggiornato/i
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Location Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Posizione
                  </label>
                  <Select
                    options={locationOptions}
                    value={selectedLocationId}
                    onChange={(e) => setSelectedLocationId(e.target.value)}
                  />
                  {locations.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Nessuna posizione disponibile. Creane una dalla gestione posizioni.
                    </p>
                  )}
                </div>

                {/* Wine Names Preview */}
                {wineNames && wineNames.length > 0 && wineNames.length <= 5 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      {wineIds.length === 1 ? 'Vino selezionato:' : 'Vini selezionati:'}
                    </p>
                    <ul className="space-y-1">
                      {wineNames.map((name, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {wineIds.length > 5 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">
                      {wineIds.length} vini selezionati
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!success && (
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <Button
                onClick={handleAssign}
                disabled={loading || !selectedLocationId || (selectedLocationId !== 'remove' && locations.length === 0)}
                className="flex-1"
                icon={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : undefined}
              >
                {loading ? 'Assegnazione...' : 'Assegna'}
              </Button>
              <Button
                onClick={handleClose}
                variant="outline"
                disabled={loading}
              >
                Annulla
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
